import { DNA, CreatureTraits } from './DNA';
import { Food } from './Food';

export interface Position {
  x: number;
  y: number;
}

export interface EnvironmentalConditions {
  temperature: number;
  resources: number;
  hazards: number;
}

export interface WorldBounds {
  width: number;
  height: number;
  environmentalConditions: EnvironmentalConditions;
  food?: Food[];
}

export interface CreatureState {
  id: string;
  dna: string;
  traits: CreatureTraits;
  position: Position;
  energy: number;
  age: number;
  isAlive: boolean;
  environmentalAdaptation: number;
}

export class Creature {
  private static nextId = 0;
  private readonly id: string;
  private readonly traits: CreatureTraits;
  private position: Position;
  private energy: number;
  private age: number;
  private isAlive: boolean;
  private environmentalAdaptation: number;

  constructor(
    private readonly dna: string,
    position: Position = { x: 0, y: 0 },
    energy: number = 100
  ) {
    this.id = `creature-${Creature.nextId++}`;
    this.traits = new DNA(dna).analyze();
    this.position = position;
    this.energy = energy;
    this.age = 0;
    this.isAlive = true;
    this.environmentalAdaptation = 1.0;
  }

  public update(deltaTime: number, worldBounds: WorldBounds): void {
    if (!this.isAlive) return;

    // Age the creature
    this.age += deltaTime;

    // Apply environmental effects
    this.applyEnvironmentalEffects(worldBounds.environmentalConditions);

    // Consume energy based on metabolism and environmental adaptation
    this.energy -= (this.traits.metabolism * deltaTime) / this.environmentalAdaptation;

    // Die if out of energy or from environmental hazards
    if (this.energy <= 0 || (Math.random() < worldBounds.environmentalConditions.hazards * 0.01)) {
      this.isAlive = false;
      return;
    }

    // Move randomly based on traits and environmental conditions
    this.move(deltaTime, worldBounds);
  }

  public applyEnvironmentalEffects(conditions: EnvironmentalConditions): void {
    // Calculate adaptation score based on traits and conditions
    let adaptationScore = 1.0;

    // Temperature adaptation based on body size
    const tempDiff = Math.abs(0.5 - conditions.temperature);
    if (this.traits.bodySize > 1.0) {
      // Larger creatures handle cold better
      adaptationScore *= 1.2 - tempDiff;
    } else {
      // Smaller creatures handle heat better
      adaptationScore *= 1.2 - (0.5 - tempDiff);
    }

    // Resource adaptation based on metabolism
    if (conditions.resources < 0.5 && this.traits.metabolism < 1.0) {
      adaptationScore *= 1.2; // Efficient metabolism helps in scarce resources
    }

    // Special abilities provide adaptation bonuses
    if (this.traits.specialAbilities.includes('camouflage')) {
      adaptationScore *= 1.2; // Better survival against hazards
    }
    if (this.traits.specialAbilities.includes('shield')) {
      adaptationScore *= 1.1; // General protection
    }

    // Complexity provides slight adaptation bonus
    adaptationScore *= (1 + this.traits.complexity * 0.2);

    this.environmentalAdaptation = adaptationScore;
  }

  private move(deltaTime: number, worldBounds: WorldBounds): void {
    const baseSpeed = 50 * (1 / this.traits.bodySize) * this.traits.behaviorTraits.speed;
    const environmentalSpeedModifier = 1 + (worldBounds.environmentalConditions.temperature - 0.5);
    const speed = baseSpeed * environmentalSpeedModifier;

    // Calculate movement based on traits and eye letter
    let dx = 0, dy = 0;
    
    // Find nearest food
    const nearestFood = this.findNearestFood(worldBounds);
    
    if (nearestFood) {
      // Calculate direction to food
      const angle = Math.atan2(
        nearestFood.position.y - this.position.y,
        nearestFood.position.x - this.position.x
      );
      
      // Move towards food with some randomness based on intelligence
      const randomFactor = 1 - (this.traits.behaviorTraits.intelligence * 0.5);
      const randomAngle = (Math.random() - 0.5) * Math.PI * randomFactor;
      
      dx = Math.cos(angle + randomAngle) * speed * deltaTime;
      dy = Math.sin(angle + randomAngle) * speed * deltaTime;
    } else {
      // Random movement when no food is visible
      const angle = Math.random() * Math.PI * 2;
      dx = Math.cos(angle) * speed * deltaTime;
      dy = Math.sin(angle) * speed * deltaTime;
    }

    // Apply movement with boundary checking
    this.position.x = Math.max(0, Math.min(this.position.x + dx, worldBounds.width));
    this.position.y = Math.max(0, Math.min(this.position.y + dy, worldBounds.height));
  }

  private findNearestFood(worldBounds: WorldBounds): Food | null {
    if (!worldBounds.food || worldBounds.food.length === 0) return null;

    let nearestFood: Food | null = null;
    let minDistance = Infinity;

    worldBounds.food.forEach(food => {
      const distance = Math.sqrt(
        Math.pow(food.position.x - this.position.x, 2) +
        Math.pow(food.position.y - this.position.y, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestFood = food;
      }
    });

    // Only return food if it's within vision range (based on eye letter)
    const visionRange = this.getVisionRange();
    return minDistance <= visionRange ? nearestFood : null;
  }

  private getVisionRange(): number {
    // Vision range based on eye letter and intelligence
    const baseRange = 100;
    const intelligenceBonus = this.traits.behaviorTraits.intelligence * 50;
    
    switch (this.traits.eyeLetter) {
      case 'V': return baseRange + intelligenceBonus;
      case 'N': return baseRange * 1.5 + intelligenceBonus;
      case 'K': return baseRange * 2 + intelligenceBonus;
      default: return baseRange;
    }
  }

  public canReproduce(): boolean {
    // Reproduction threshold based on metabolism and complexity
    const baseThreshold = 50;
    const metabolismFactor = this.traits.metabolism;
    const complexityFactor = this.traits.complexity;
    
    return this.isAlive && 
           this.energy >= baseThreshold * metabolismFactor * (1 + complexityFactor) && 
           this.age >= 10;
  }

  public reproduce(partner?: Creature): Creature | undefined {
    if (!this.canReproduce()) return undefined;

    // Reproduction cost based on complexity
    const baseCost = 0.7;
    const complexityCost = this.traits.complexity * 0.1;
    this.energy *= (baseCost + complexityCost);
    
    let childDna: string;
    if (partner && partner.canReproduce()) {
      // Sexual reproduction - mix DNA
      const splitPoint = Math.floor(Math.random() * this.dna.length);
      childDna = this.dna.slice(0, splitPoint) + partner.getDNA().slice(splitPoint);
      partner.energy *= (baseCost + complexityCost);
    } else {
      // Asexual reproduction - mutate own DNA
      childDna = this.dna;
    }

    // Apply mutations with rate based on environmental stress and complexity
    const baseMutationRate = 0.1;
    const stressFactor = 1 / this.environmentalAdaptation;
    const complexityFactor = this.traits.complexity;
    const mutationRate = baseMutationRate * stressFactor * (1 + complexityFactor);
    
    childDna = DNA.mutate(childDna, mutationRate);

    return new Creature(
      childDna,
      { ...this.position },
      this.energy * 0.3
    );
  }

  public consumeFood(amount: number): void {
    if (!this.isAlive) return;
    
    // Metabolism affects how efficiently food is converted to energy
    const efficiency = 1 / this.traits.metabolism;
    const energyGain = amount * efficiency;
    
    this.energy = Math.min(100, this.energy + energyGain);
  }

  public getState(): CreatureState {
    return {
      id: this.id,
      dna: this.dna,
      traits: this.traits,
      position: this.position,
      energy: this.energy,
      age: this.age,
      isAlive: this.isAlive,
      environmentalAdaptation: this.environmentalAdaptation
    };
  }

  public getDNA(): string {
    return this.dna;
  }

  public getPosition(): Position {
    return { ...this.position };
  }
} 